import { FILTERS_MAP } from './config';
import Filter from 'Components/filters/Filter';
import {
  CAT_PRODUCTS,
  SUB_PRODUCTS_VAPING_DEV,
  CAT_BRANDS,
  CAT_COMPANIES,
  SUB_PRODUCTS_WNS_DEV,
} from 'App/utils/Collections';

/**
 * @class FilterBox
 */
export default class FilterBox {
  /**
   * @param {VueRouter} router
   * @param {Store} store
   * @param {string=production} [mode]
   */
  constructor(router, store, mode = 'production') {
    this._router = router;
    this._store = store;
    this._mode = mode;
    /** @type {Map<string, Filter>} */
    this.filters = new Map();
    this.isActiveUserFilter = false;
  }

  hasModuleFilterConfig = (category) => {
    return Boolean(FILTERS_MAP[category]);
  };

  /**
   * Creates filter for category with predefined config
   * @param {string} category
   * @param {string} [key]
   * @param {boolean} [force]
   * @returns {Filter}
   */
  addModuleFilter = (category, key, force = false) => {
    const conf = FILTERS_MAP[category];
    if (!conf) {
      throw new Error(`Can not find filter configuration for category "${category}".`);
    }
    const _key = key || category;
    return this.createFilter(_key, conf, force);
  };

  /**
   * @param {string} key
   * @param {object} conf
   * @param {boolean} force
   * @returns {Filter}
   */
  createFilter = (key, conf, force = false) => {
    if (force || !this.filters.has(key)) {
      const filter = new Filter(this._store, conf, this._mode);
      this.filters.set(key, filter);
    }
    return this.filters.get(key);
  };

  /**
   * @param {string} key
   * @return {Filter}
   */
  getFilter = (key) => {
    return this.filters.get(key);
  };

  /**
   * @param {string} filterKey
   * @param {string} itemKey
   * @return {FilterItem|null}
   */
  getFilterItem = (filterKey, itemKey) => {
    const filter = this.getFilter(filterKey);
    return filter?.getItem(itemKey) || null;
  };

  resetFilters = (key) => {
    const filter = this.filters.get(key);
    if (filter) {
      filter.deactivateAll();
      filter.active = false;
    }
  };

  navWithFilter = async (category, key, filter, to) => {
    // Navigation to another listing starts from scratch: a search term left on that
    // page's cached filter must not travel with the conditions we are applying here.
    const appliedFilter = await this.setFilters(category, key, filter, '');
    if (to) {
      const destination = this.buildFilterDestination({ conditions: filter }, to, appliedFilter);
      return this._router.push(destination).catch(() => {});
    }
    return appliedFilter;
  };

  /**
   * Reads the "navigation came from a user filter" flag and clears it, so the
   * flag describes only the navigation that is being handled right now.
   * @return {boolean}
   */
  consumeActiveUserFilterFlag = () => {
    const { isActiveUserFilter } = this;
    this.isActiveUserFilter = false;
    return isActiveUserFilter;
  };

  navWithUserFilter = async (userFilter, to, classification) => {
    this.isActiveUserFilter = true;
    const filter = await this.setUserFilter(userFilter, classification);
    if (to) {
      const destination = this.buildFilterDestination(userFilter, to, filter);
      this._router.push(destination).catch(() => {});
    }
  };

  /**
   * @param {object} userFilter
   * @param {string|object} to
   * @param {Filter} [filter]
   * @return {string|object}
   */
  buildFilterDestination = (userFilter, to, filter = null) => {
    const query = this.buildUserFilterQuery(userFilter, filter);
    return this.prepareDestinationWithQuery(to, query);
  };

  /**
   * Href of a filtered listing, for "open in new tab" flows. Stays synchronous
   * (no filter is applied here) so `window.open` keeps the user gesture and is
   * not blocked — the target tab re-creates its filters from the URL query.
   * @param {object} userFilter
   * @param {string|object} to
   * @param {string} [key] key of an already created filter, for feature-key resolution
   * @return {string}
   */
  buildFilterHref = (userFilter, to, key = null) => {
    const destination = this.buildFilterDestination(userFilter, to, key ? this.getFilter(key) : null);
    return this._router.resolve(destination).href;
  };

  buildUserFilterQuery = (userFilter, filter = null) => {
    const conditions = userFilter?.conditions || {};

    const resolveFeatureTarget = (featureId, isNot = false) => {
      if (!filter?.items?.length) {
        return {
          key: isNot ? `not_feature${featureId}` : `feature${featureId}`,
          serializeEntries: false,
        };
      }

      const candidates = filter.items.filter(
        (item) => item?.isFeature && String(item?.featureID) === String(featureId)
      );
      if (!candidates.length) {
        return {
          key: isNot ? `not_feature${featureId}` : `feature${featureId}`,
          serializeEntries: false,
        };
      }

      const conditionGroupItem = candidates.find((item) => item.type === 'condition-group');
      if (conditionGroupItem) {
        return {
          key: conditionGroupItem.id,
          serializeEntries: true,
        };
      }

      if (isNot) {
        const notItem = candidates.find((item) => String(item.id).startsWith('not_feature'));
        return {
          key: notItem?.id || candidates[0].id,
          serializeEntries: false,
        };
      }

      const plainFeatureItem = candidates.find((item) => !String(item.id).startsWith('not_feature'));
      return {
        key: plainFeatureItem?.id || candidates[0].id,
        serializeEntries: false,
      };
    };

    const resolveFeatureQueryKey = (rawKey) => {
      const featureMatch = rawKey.match(/^(not_)?feature(\d+)/);
      if (!featureMatch) {
        return rawKey;
      }
      return resolveFeatureTarget(String(featureMatch[2]), Boolean(featureMatch[1])).key;
    };

    const mergeQueryValue = (target, key, value) => {
      if (!(key in target)) {
        target[key] = value;
        return;
      }
      const current = target[key];
      const currentValues = Array.isArray(current) ? current : [current];
      const nextValues = Array.isArray(value) ? value : [value];
      target[key] = [...currentValues, ...nextValues];
    };

    const conditionGroupFeatureEntries = {};

    const query = Object.entries(conditions).reduce((acc, [key, value]) => {
      if (key === '__feature' || key === 'features') {
        let parsed = value;
        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch (e) {
            parsed = [];
          }
        }
        const entries = Array.isArray(parsed) ? parsed : parsed && typeof parsed === 'object' ? [parsed] : [];
        entries.forEach((entry) => {
          const featureKey = Object.keys(entry || {}).find((entryKey) => entryKey !== 'not');
          if (!featureKey) {
            return;
          }

          const { key: mappedKey, serializeEntries } = resolveFeatureTarget(featureKey, Boolean(entry?.not));

          if (serializeEntries) {
            if (!conditionGroupFeatureEntries[mappedKey]) {
              conditionGroupFeatureEntries[mappedKey] = [];
            }
            conditionGroupFeatureEntries[mappedKey].push(entry);
            return;
          }

          mergeQueryValue(acc, mappedKey, entry[featureKey]);
        });
        return acc;
      }

      if (key.startsWith('__')) {
        return acc;
      }
      if (value === undefined || value === null || value === '') {
        return acc;
      }

      const normalizedKey = resolveFeatureQueryKey(key);
      mergeQueryValue(acc, normalizedKey, value);
      return acc;
    }, {});

    Object.entries(conditionGroupFeatureEntries).forEach(([key, entries]) => {
      query[key] = JSON.stringify(entries);
    });

    const searchString = typeof userFilter?.searchString === 'string' ? userFilter.searchString.trim() : '';
    if (searchString) {
      query.searchString = searchString;
    }

    return query;
  };

  prepareDestinationWithQuery = (to, query) => {
    if (!query || Object.keys(query).length === 0) {
      return to;
    }

    if (typeof to === 'string') {
      const resolved = this._router.resolve(to);
      return {
        path: resolved.route.path,
        hash: resolved.route.hash,
        query: {
          ...(resolved.route.query || {}),
          ...query,
        },
      };
    }

    return {
      ...to,
      query: {
        ...(to.query || {}),
        ...query,
      },
    };
  };

  /**
   * Omitting `searchString` applies the conditions on top of the search bar state the filter
   * already holds; passing it (an empty string included) overwrites that state.
   * @param {string} category
   * @param {string} key
   * @param {object} filters
   * @param {string} [searchString]
   * @return {Promise<Filter>}
   */
  setFilters = async (
    category,
    key,
    filters,
    searchString,
    strict = false,
    isSpec = false,
    isDecodedFlavors = false,
    isDescription = false,
    isFlavorClaims = false,
    isChemicalFormula = false,
    isClaims = false
  ) => {
    const filter = this.addModuleFilter(category, key);
    if (filter) {
      await filter.init();
      if (searchString === undefined) {
        await filter.applyConditions(filters);
      } else {
        await filter.applyFilter(
          filters,
          searchString,
          strict,
          isSpec,
          isDecodedFlavors,
          isDescription,
          isFlavorClaims,
          isChemicalFormula,
          isClaims
        );
      }
      filter.active = true;
    }
    return filter;
  };

  setUserFilter = async (userFilter, classification) => {
    const { __filtersKey } = userFilter?.conditions;
    const moduleCategory = userFilter?.moduleAlias || userFilter?.category;

    if (!moduleCategory) {
      throw new Error('Can not resolve user filter category from moduleAlias/category.');
    }

    let key = __filtersKey;
    if (!key) {
      key =
        moduleCategory === CAT_PRODUCTS || moduleCategory === CAT_BRANDS || moduleCategory === CAT_COMPANIES
          ? `${moduleCategory}::${classification === 'Tincture' ? SUB_PRODUCTS_WNS_DEV : SUB_PRODUCTS_VAPING_DEV}`
          : moduleCategory;
    }
    const filter = this.addModuleFilter(moduleCategory, key);
    if (filter) {
      await filter.init();
      await filter.applyUserFilter(userFilter);
      filter.active = true;
    }
    return filter;
  };

  saveStoredFilters = (key, filter) => {
    return this._store.commit('setFilter', { key, data: filter });
  };

  updateFilters = (key, filter) => {
    return this._store.dispatch('updateFilter', { key, data: filter });
  };

  clearFilters = (key) => {
    this._store.dispatch('clearFilter', key);
  };

  getFilters = (key) => {
    const { [key]: filters } = this._store.state.filters || {};
    return {
      __searchbar__: '',
      __pager__: { itemsPerPage: 30, page: 1 },
      ...(filters || {}),
    };
  };
}
